import {
  useHFStore,
  useIndicatorStore,
  useLayerStore,
  useLGAStore,
  useStateStore,
  useUtilStore,
  useWardStore,
  useZoneStore,
} from "@/data/Stores";
import { ContentType, LocationLevelEnum, MethodTypeEnum, VisualizationTypeEnum } from "@/enums";
import {
  IBoundary,
  IGeoData,
  IMaybe,
  IndicatorData,
  INotSure,
  IPhcQuery,
} from "@/types";
import { ApiUrl } from "@/utils/ApiNames";
import { AppRequest } from "@/utils/AppRequest";
import {
  boundaryDS,
  countryId,
  dashboardDS,
  dateFormats,
  lastUpdated,
  maxValueDS,
  mMortality,
  PHC,
} from "@/utils/Constant";
import AppLogger from "@/utils/Logger";
import { indicatorQueryBuilder } from "@/utils/Misc";
import { getIdxValue, ILastUpdated, keepDataFresh, storeIdxValue } from "@/utils/Storage";
import dayjs from "dayjs";
import IndicatorApi from "./Indicator";

/**
 * @function getBoundary
 * @description Get Boundary
 */
const getBoundary = async (
  id: string,
  level: LocationLevelEnum
): Promise<IBoundary> => {
  const urlMap = {
    [LocationLevelEnum.COUNTRY]: `${ApiUrl.CountryBoundary}${id}/`,
    [LocationLevelEnum.ZONE]: `${ApiUrl.ZoneBoundary}${id}/`,
    [LocationLevelEnum.STATE]: `${ApiUrl.StateBoundary}${id}/`,
    [LocationLevelEnum.LGA]: `${ApiUrl.LgaBoundary}${id}/`,
    [LocationLevelEnum.WARD]: `${ApiUrl.WardBoundary}${id}/`,
    [LocationLevelEnum.HF]: `${ApiUrl.FacilityBoundary}${id}/`,
  };

  const url = urlMap[level];
  if (!url) throw new Error(`Invalid location level: ${level}`);

  return await AppRequest(url);
};

/**
 * @function getIndicator
 * @description Get Indicator
 */
const getIndicator = async (id: string): Promise<IndicatorData> => {
  const response = await AppRequest(`${ApiUrl.Indicators + id}/`);
  return response;
};

/**
 * @function getPHC
 * @description Get PHC
 */
const getPHC = async ({
  id,
  query,
}: {
  id?: IMaybe<string>;
  query?: IMaybe<string>;
}): Promise<IBoundary | { data: IGeoData[] }> => {
  const response = await AppRequest(
    query
      ? `${ApiUrl.FacilityBoundary}${query}`
      : `${ApiUrl.FacilityBoundary + id}/`
  );
  return response;
};

/**
 * @function getPHCs
 * @description Get PHCs
 */
const getPHCs = async (query = ""): Promise<{ data: IGeoData[] }> => {
  const response = await AppRequest(`${ApiUrl.FacilityBoundary}${query}`);
  return response;
};

/**
 * @function createFeedback
 * @description Create feedback
 */
const createFeedback = async (data: INotSure) => {
  const response = await AppRequest(
    `${ApiUrl.FeedBack}`,
    MethodTypeEnum.POST,
    data,
    true,
    50_000,
    ContentType.MULTIPART
  );
  return response;
};

/**
 * @function queryPhc
 * @description Query PHC
 */
const queryPhc = async (query: string): Promise<INotSure> => {
  const response = await AppRequest(`${ApiUrl.QueryPhc}?${query}`);
  return response;
};

/**
 * @function getLastUpdated
 * @description Get last updated
 */
// Helper function to determine the latest date
const getLatestDate = (dates: (string | undefined)[]) => {
  //  remove null from dates
  const removeNull = dates?.filter((el) => el != null);

  return removeNull
    ?.map((date) => dayjs(date))
    ?.filter((date) => date?.isValid())
    ?.reduce(
      (latest, current) => (current?.isAfter(latest) ? current : latest),
      dayjs(0)
    );
};

const getLastUpdated = async (): Promise<ILastUpdated> => {
  try {
    const response = await AppRequest(ApiUrl.LastUpdated);
    if (response?.status !== 200) {
      throw new Error("Failed to fetch last updated data");
    }
    if (!response?.data || response?.data?.count === 0) {
      throw new Error("No data found");
    }
    if (!response?.data?.last_updated) {
      throw new Error("No last updated date found");
    }

    const latestDate = getLatestDate([
      response?.data?.last_updated,
      response?.data?.last_updated_phc,
      response?.data?.last_updated_revitalization,
    ]);

    const expiryDate = latestDate.add(0, "day").toDate();
    const formattedExpiry = dayjs(expiryDate).format(dateFormats.DATE_TIME);

    const phc_last_updated = response?.data?.last_updated_phc
      ? dayjs(response?.data?.last_updated_phc).format(dateFormats.DATE_TIME)
      : "";
    const rev_last_updated = response?.data?.last_updated_revitalization
      ? dayjs(response?.data?.last_updated_revitalization).format(
          dateFormats.DATE_TIME
        )
      : "";
    const last_updated = response?.data?.last_updated
      ? dayjs(response?.data?.last_updated).format(dateFormats.DATE_TIME)
      : "";

    const resultData = {
      total_count: response?.count ?? 0,
      last_updated,
      phc_last_updated,
      rev_last_updated,
      data_last_updated: formattedExpiry,
    };

    const storedData = await getIdxValue<{ data_last_updated: string }>(
      lastUpdated
    );
    if (!storedData) {
      AppLogger.info("No data found in IndexedDB");
      await storeIdxValue(lastUpdated, resultData);
    }

    keepDataFresh(resultData);
    return resultData;
  } catch (error: INotSure) {
    throw new Error(`Error fetching last updated data: ${error.message}`);
  }
};

/**
 * @function buildBoundaryQuery
 * @description Build Boundary Query
 */
function isIPhcQuery(data: IBoundary | IPhcQuery): data is IPhcQuery {
  return (data as IPhcQuery)?.count !== undefined;
}

const buildBoundaryQuery = async (phcField: IMaybe<string> = null) => {
  const { selectedZone } = useZoneStore.getState();
  const { selectedState } = useStateStore.getState();
  const { selectedLga } = useLGAStore.getState();
  const { selectedWard } = useWardStore.getState();
  const { setPhcData, setPhcQuery } = useHFStore.getState();
  const { visualizationType, idxLoad, setIsLoading } =
    useUtilStore.getState();

  const {
    setDashboardData,
    clearBoundaryByIndicator,
    setIndicatorData,
    indicatorType,
    selectedIndicator,
  } = useIndicatorStore.getState();

  const { loadBoundary, setMaxValue, resetLayer } = useLayerStore.getState();

  setIsLoading(true);

  if (!indicatorType || indicatorType.length === 0)
    return clearBoundaryByIndicator();

  const getQueryParams = () => {
    if (selectedWard)
      return { param: `ward=${selectedWard}`, level: LocationLevelEnum.WARD };
    if (selectedLga)
      return { param: `lga=${selectedLga}`, level: LocationLevelEnum.LGA };
    if (selectedState)
      return { param: `state=${selectedState}`, level: LocationLevelEnum.STATE };
    if (selectedZone)
      return { param: `zone=${selectedZone}`, level: LocationLevelEnum.ZONE };
    return { param: `country=${countryId}`, level: LocationLevelEnum.COUNTRY };
  };

  const noIndicator = !selectedIndicator || selectedIndicator.length === 0;

  const { param: locationQuery, level: queryLevel } = getQueryParams();

  const { queryString, facilityTypeString, facilityOwnerString, facilityStatusString, facilityFundingString } =
    indicatorQueryBuilder(selectedIndicator);

  const buildPayload = () => {
    const basePayload = `?geo_json=${
      visualizationType === VisualizationTypeEnum.MAP ||
      visualizationType === VisualizationTypeEnum.GRID
    }&${locationQuery}`;

    if (noIndicator) return basePayload;

    return `${basePayload}&category=${queryString}&facility_type=${facilityTypeString}&facility_owner=${facilityOwnerString}&facility_status=${facilityStatusString}&facility_funding=${facilityFundingString}`;
  };

  const _payload = buildPayload();

  const hasMortality = indicatorType?.includes(mMortality);

  if (!indicatorType?.includes(PHC)) return setIsLoading(false);

  const $payload = hasMortality ? `${_payload}&mortality=true` : _payload;
  const payload = phcField ? `${$payload}&phc_field=${phcField}` : $payload;

  if (!payload) throw new Error("Boundary query could not be built.");

  try {
    const _noSelectedIndicator = !selectedIndicator || selectedIndicator.length === 0;
    const hasLocalData = await getIdxValue(maxValueDS);
    if (
      queryLevel === LocationLevelEnum.COUNTRY &&
      !phcField &&
      window.indexedDB &&
      typeof hasLocalData === "number" && _noSelectedIndicator
    ) {
       await idxLoad();
       return;
    }

    const $BoundaryData: IBoundary | IPhcQuery =
      await IndicatorApi.queryByIndicator(PHC, payload);

    if (phcField && isIPhcQuery($BoundaryData)) {
      setPhcData($BoundaryData);
      setPhcQuery(null);
      setIsLoading(false);
      return;
    }

    const { features } = $BoundaryData?.data ?? { features: [] };

    let maxValue = 0;
    const stateData = features?.map((feature) => {
      const properties = feature?.properties || {};
      maxValue = Math.max(maxValue, properties.health_facility_count ?? 0);
      return properties;
    });

    const $dashboardData = {
      staff_accommodation_availability:
        $BoundaryData?.data?.staff_accommodation_availability || 0,
      total_bhcpf_impact_funds:
        $BoundaryData?.data?.total_bhcpf_impact_funds || 0,
      total_count: $BoundaryData?.data?.total_count || 0,
      total_facility_functionality_1:
        $BoundaryData?.data?.total_facility_functionality_1 || 0,
      total_facility_functionality_2:
        $BoundaryData?.data?.total_facility_functionality_2 || 0,
      total_facility_functionality_partial:
        $BoundaryData?.data?.total_facility_functionality_partial || 0,
      total_number_of_chos: $BoundaryData?.data?.total_number_of_chos || 0,
      total_number_of_jchews: $BoundaryData?.data?.total_number_of_jchews || 0,
      total_number_of_lab_techs:
        $BoundaryData?.data?.total_number_of_lab_techs || 0,
      total_number_of_medical_officers:
        $BoundaryData?.data?.total_number_of_medical_officers || 0,
      total_number_of_nurses: $BoundaryData?.data?.total_number_of_nurses || 0,
      total_number_of_pharmacy_techs:
        $BoundaryData?.data?.total_number_of_pharmacy_techs || 0,
      total_number_of_sba: $BoundaryData?.data?.total_number_of_sba || 0,
      total_number_of_schews: $BoundaryData?.data?.total_number_of_schews || 0,
      total_sba_gaps: $BoundaryData?.data?.total_sba_gaps || 0,
      functional_backup_power:
        $BoundaryData?.data?.functional_backup_power || 0,
      phc_count: stateData,
    };

    // ========= UPDATE THE ZUSTAND STATE
    resetLayer();
    setDashboardData($dashboardData);
    setIndicatorData($BoundaryData ?? null);
    if ($BoundaryData) loadBoundary([$BoundaryData], queryLevel);
    setMaxValue(maxValue);

    // ========= UPDATE THE INDEXED DB
    if (queryLevel === LocationLevelEnum.COUNTRY && _noSelectedIndicator) {
      await storeIdxValue(boundaryDS, [$BoundaryData]);
      await storeIdxValue(dashboardDS, $dashboardData);
      await storeIdxValue(maxValueDS, maxValue);
    }
  } catch (error) {
    AppLogger.error(
      "Error on buildBoundaryQuery =>",
      error as Error,
    );
  } finally {
    setIsLoading(false);
  }
};

const BoundaryApi = {
  getBoundary,
  getIndicator,
  getPHC,
  getPHCs,
  createFeedback,
  queryPhc,
  buildBoundaryQuery,
  getLastUpdated,
};

export default BoundaryApi;
